import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import * as WindowHelper from '@Services/WindowHelper';
import * as BrowserHelper from '@Services/BrowserHelper';
import TableHeader from '@Components/Results/TableHeader';

import './sticky-table-header.scss';

const opts = {
  hideOffset: 150,
  updateDelay: 400,
};

const isIE = BrowserHelper.detectIE();

export default class StickyTableHeaderComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.update = this.update.bind(this);
    this.updateTableHeader = this.updateTableHeader.bind(this);
    this.updateTableHeaderDebounced = throttle(this.updateTableHeader, 10);
    this.updateCellsWidth = this.updateCellsWidth.bind(this);
    this.showHiddenColumns = this.showHiddenColumns.bind(this);

    this.delayedShowUpIE = debounce((clonedHeader) => clonedHeader.classList.remove('transparent'), 500);
    this.unsubscribeOnResize = null;

    this.theadRefOriginal = React.createRef();
    this.theadRefCloned = React.createRef();
  }

  componentDidMount() {
    const { scrollContainerSelector } = this.props;
    this.scrollbarContainer = scrollContainerSelector ? document.querySelector(scrollContainerSelector) : window;

    if (this.scrollbarContainer) {
      this.scrollbarContainer.addEventListener('scroll', this.updateTableHeaderDebounced);
    }

    this.resultsHeader = null;

    this.unsubscribeOnResize = WindowHelper.onResize(throttle(this.update, 100));
  }

  componentDidUpdate(prevProps) {
    const { headers } = this.props;
    if (!isEqual(headers, prevProps.headers)) {
      // update table header and cell width after adding/removing columns in switcher
      this.update();
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeOnResize) this.unsubscribeOnResize();
    if (this.scrollbarContainer) this.scrollbarContainer.removeEventListener('scroll', this.updateTableHeaderDebounced);
  }

  showHiddenColumns() {
    const { showAllNonEmptyCols } = this.props;
    if (showAllNonEmptyCols) showAllNonEmptyCols();
    this.update();
  }

  update() {
    setTimeout(() => {
      this.updateTableHeader();
      this.updateCellsWidth();
    }, opts.updateDelay);
  }

  updateTableHeader() {
    const theadRefOriginal = this.theadRefOriginal.current;
    const theadRefCloned = this.theadRefCloned.current;
    // eslint-disable-next-line react/no-find-dom-node
    const table = theadRefOriginal?.closest('table');
    const elementRect = table.getBoundingClientRect();
    const scrollbarRect =
      this.scrollbarContainer !== window ? this.scrollbarContainer?.getBoundingClientRect() : { top: window.pageYOffset };

    if (!table.closest('.modal-dialog')) {
      this.resultsHeader = this.resultsHeader || document.querySelector('.result-toolbar');
    } else {
      this.resultsHeader = null;
    }

    const resultsHeaderHeight = this.resultsHeader ? this.resultsHeader.offsetHeight : 0;
    const resultsHeaderRect = this.resultsHeader
      ? this.resultsHeader.getBoundingClientRect()
      : {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        };

    function prepareHeader() {
      this.updateCellsWidth();
      theadRefCloned?.classList?.add('sticky');
      if (isIE) {
        // IE has some glitches. we hide header while user is scrolling
        theadRefCloned?.classList?.add('transparent');
        this.delayedShowUpIE(theadRefCloned);
      }
    }

    if (
      this.scrollbarContainer === window &&
      elementRect.top < resultsHeaderRect.bottom &&
      elementRect.bottom - opts.hideOffset > resultsHeaderRect.bottom
    ) {
      theadRefCloned.style.top = `${resultsHeaderRect.bottom - elementRect.top}px`;
      prepareHeader.call(this);
    } else if (
      this.scrollbarContainer !== window &&
      elementRect.top < scrollbarRect.top + resultsHeaderHeight &&
      elementRect.bottom - opts.hideOffset > scrollbarRect.top + resultsHeaderHeight
    ) {
      theadRefCloned.style.top = `${scrollbarRect.top - elementRect.top + resultsHeaderHeight}px`;
      prepareHeader.call(this);
    } else {
      theadRefCloned?.classList?.remove('sticky');
    }
  }

  updateCellsWidth() {
    const originalHeaderThs = this.theadRefOriginal.current?.querySelectorAll('tr.auto-resize th');
    const clonedHeaderThs = this.theadRefCloned.current?.querySelectorAll('tr.auto-resize th');

    clonedHeaderThs.forEach((el, index) => {
      const { width } = originalHeaderThs?.[index]?.getBoundingClientRect();
      // eslint-disable-next-line no-param-reassign
      el.style.minWidth = `${width}px`;
      // eslint-disable-next-line no-param-reassign
      el.style.maxWidth = `${width}px`;
    });
  }

  render() {
    const { headers, children, staticColumns } = this.props;

    return (
      <>
        <TableHeader
          className=""
          getRef={this.theadRefOriginal}
          headers={headers}
          staticColumns={staticColumns}
          showHiddenColumns={this.showHiddenColumns}>
          {children}
        </TableHeader>
        <TableHeader
          className="cloned-header"
          getRef={this.theadRefCloned}
          headers={headers}
          staticColumns={staticColumns}
          showHiddenColumns={this.showHiddenColumns}>
          {children}
        </TableHeader>
      </>
    );
  }
}

StickyTableHeaderComponent.propTypes = {
  headers: PropTypes.array.isRequired,
  scrollContainerSelector: PropTypes.string,
  showAllNonEmptyCols: PropTypes.func,
  children: PropTypes.node,
  staticColumns: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};
